import { Injectable, inject } from '@angular/core';
import { explainedInPlace, rethrowAsRefusal } from '@core/helpers/installation-refusal.helper';
import { utcIso } from '@core/helpers/slot-series.helper';
import { LocationModel } from '@core/models/location.model';
import {
  CoordinateResult,
  InstallationZoneChange,
  PreviewCoordinates,
  SetCoordinates,
} from '@core/models/location-coordinates.model';
import { ApiService } from '@core/services/api.service';
import { Observable, catchError, map } from 'rxjs';

// customer-func serialises its Decimal coordinates as strings.
interface CoordinateResultResponse {
  latitude: string | number | null;
  longitude: string | number | null;
  timezone: string | null;
  refusal: string | null;
  installations: InstallationZoneChange[];
}

function parsed(result: CoordinateResultResponse): CoordinateResult {
  return {
    ...result,
    latitude: result.latitude === null ? null : Number(result.latitude),
    longitude: result.longitude === null ? null : Number(result.longitude),
  };
}

// Every request fails with an InstallationRefusal.
@Injectable({
  providedIn: 'root',
})
export class LocationCoordinatesService {
  private readonly apiService = inject(ApiService);

  // The single read carries the placement provenance the list read leaves out.
  location(locationId: number): Observable<LocationModel> {
    return this.apiService
      .getRaw<LocationModel>(`/locations/${locationId}`, {}, explainedInPlace())
      .pipe(
        map(location => ({
          ...location,
          coordinateSetAt: location.coordinateSetAt ? utcIso(location.coordinateSetAt) : null,
        })),
        catchError(rethrowAsRefusal),
      );
  }

  // Resolves the zone each Active installation would get, and writes nothing.
  preview(locationId: number, body: PreviewCoordinates): Observable<CoordinateResult> {
    return this.apiService
      .postRaw<CoordinateResultResponse>(`/locations/${locationId}/coordinates/preview`, body, {
        context: explainedInPlace(),
      })
      .pipe(map(parsed), catchError(rethrowAsRefusal));
  }

  set(locationId: number, body: SetCoordinates): Observable<CoordinateResult> {
    return this.apiService
      .putRaw<CoordinateResultResponse>(
        `/locations/${locationId}/coordinates`,
        body,
        explainedInPlace(),
      )
      .pipe(map(parsed), catchError(rethrowAsRefusal));
  }
}
